import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { getSettingKey } from "../../modules/Common/utils";
import KaiAvatar from "./KaiAvatar";
import { API_BASE_URL } from "../../../constants/config";
import "./KaiWidget.css";

let open = false;
const listeners = new Set();
const emit = () => listeners.forEach((fn) => fn(open));
const subscribe = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};
const toggle = () => {
  open = !open;
  emit();
};
const close = () => {
  open = false;
  emit();
};

// KAI is reached through kapp's own API (/tenant/api/v1/kai/...), which
// checks the KaiAccess permission and forwards to the KAI service; the
// browser never needs KAI's URL. This only decides whether to show the widget.
export function kaiEnabled() {
  if ((process.env.REACT_APP_KAI_ENABLED || "").trim() === "true") return true;
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  return host === "dev.ikhub.biz" || host === "stage.ikhub.biz";
}

const KAI_API = `${API_BASE_URL}/kai`;

// Shown only if /kai/welcome can't be reached — the real copy is server-side
// so it stays in step with what KAI actually supports.
const FALLBACK_WELCOME = {
  message:
    "Welcome to KAI, your business assistant.\n\nI answer questions about your own business data. Every figure comes straight from your records.",
  examples: ["How were sales last month?", "Who are my top customers this quarter?", "What do customers owe?"],
};

export function KaiHeaderButton() {
  const [isOpen, setIsOpen] = useState(open);
  useEffect(() => subscribe(setIsOpen), []);
  if (!kaiEnabled()) return null;
  return (
    <button
      type="button"
      className={`kai-header-toggle${isOpen ? " kai-header-toggle--open" : ""}`}
      onClick={toggle}
      aria-label={isOpen ? "Close KAI" : "Open KAI"}
      title="KAI"
    >
      <KaiAvatar size={28} state="idle" animated={false} />
      <span className="kai-header-name">KAI</span>
    </button>
  );
}

function replyText(body) {
  if (!body || typeof body !== "object") return "KAI did not answer.";
  return body.needs_clarification || body.answer || (typeof body.detail === "string" ? body.detail : "") || body.error || "KAI did not answer.";
}

export default function KaiWidget({ authToken, page = false }) {
  const enabled = kaiEnabled();
  const basicSettings = useSelector((state) => state.basicSettings);
  const immutable = getSettingKey(basicSettings && basicSettings.entity, "IMMUTABLE_BASIC") || {};
  const publicKey = immutable.public_key || "";
  const [isOpen, setIsOpen] = useState(open);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([]);
  const [large, setLarge] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [wipeOpen, setWipeOpen] = useState(false);
  const [wipeOk, setWipeOk] = useState(false);
  const [welcome, setWelcome] = useState(null);
  const conversationId = useRef("");
  const logRef = useRef(null);

  useEffect(() => subscribe(setIsOpen), []);
  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [messages, isOpen]);
  useEffect(() => {
    if (!enabled || !authToken) return undefined;
    let cancelled = false;
    fetch(`${KAI_API}/conversation/current`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((body) => {
        if (cancelled || !body) return;
        if (body.conversation_id) conversationId.current = body.conversation_id;
        if (Array.isArray(body.messages)) setMessages(body.messages);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [enabled, authToken]);

  // Greeting, fetched once when the panel first shows. Free on the KAI side:
  // no model call, nothing drawn from the tenant's allowance.
  const show = page || isOpen;
  useEffect(() => {
    if (!show || welcome || !enabled || !authToken) return undefined;
    let cancelled = false;
    fetch(`${KAI_API}/welcome`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((body) => {
        if (cancelled) return;
        setWelcome(body && body.message ? { message: body.message, examples: body.examples || [] } : FALLBACK_WELCOME);
      })
      .catch(() => {
        if (!cancelled) setWelcome(FALLBACK_WELCOME);
      });
    return () => {
      cancelled = true;
    };
  }, [show, welcome, enabled, authToken]);

  if (!enabled || !authToken) return null;

  const ask = async (query) => {
    const trimmed = query.trim();
    if (!trimmed || busy) return;
    const replying = replyTo;
    setText("");
    setReplyTo(null);
    setMessages((prev) => [...prev, { role: "user", text: trimmed }]);
    setBusy(true);
    try {
      const response = await fetch(`${KAI_API}/chat`, {
        method: "POST",
        headers: { Authorization: `Bearer ${authToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          query: trimmed,
          conversation_id: conversationId.current || null,
          reply_to: replying ? replying.index : null,
        }),
      });
      const body = await response.json().catch(() => null);
      if (body && body.conversation_id) conversationId.current = body.conversation_id;
      const first = body && Array.isArray(body.data) ? body.data[0] : null;
      setMessages((prev) => [...prev, {
        role: "kai",
        text: replyText(body),
        cards: first && Array.isArray(first.cards) ? first.cards : [],
        more: Boolean(first && first.has_more),
      }]);
    } catch (err) {
      setMessages((prev) => [...prev, { role: "kai", text: "KAI could not be reached." }]);
    } finally {
      setBusy(false);
    }
  };

  const openWipe = () => {
    if (busy || messages.length === 0) return;
    setWipeOk(false);
    setWipeOpen(true);
  };

  const resetHistory = async () => {
    if (!wipeOk || busy) return;
    setBusy(true);
    try {
      await fetch(`${KAI_API}/conversation/current`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setMessages([]);
      setReplyTo(null);
      setText("");
      setWipeOpen(false);
      setWipeOk(false);
    } catch (err) {
      setWipeOpen(false);
      setMessages((prev) => [...prev, { role: "kai", text: "History could not be cleared." }]);
    } finally {
      setBusy(false);
    }
  };

  const greeting = welcome || FALLBACK_WELCOME;
  return (
    <>
      {show && (
        <section className={`kai-panel${page ? " kai-panel--page" : ""}${!page && large ? " kai-panel--max" : ""}`} aria-label="KAI">
          <div className="kai-panel-head">
            <KaiAvatar size={28} state={busy ? "thinking" : "idle"} animated={busy} />
            <div>
              <div className="kai-panel-title">KAI</div>
              <div className="kai-panel-sub">Answers from your business records</div>
            </div>
            <div className="kai-panel-actions">
              <button type="button" className="kai-panel-icon" onClick={openWipe} disabled={busy || messages.length === 0} aria-label="Reset chat" title="Reset chat">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path d="M3 4.5h10M6.2 4.5V3.2h3.6v1.3M4.2 4.5l.6 8.2h6.4l.6-8.2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                </svg>
              </button>
              {!page && (
                <button type="button" className="kai-panel-icon" onClick={() => setLarge((value) => !value)} aria-label={large ? "Minimize KAI" : "Maximize KAI"} title={large ? "Minimize" : "Maximize"}>
                  {large ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                      <path fill="currentColor" d="M22 3.41 16.71 8.7 20 12h-8V4l3.29 3.29L20.59 2 22 3.41zM3.41 22l5.29-5.29L12 20v-8H4l3.29 3.29L2 20.59 3.41 22z" />
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                      <path fill="currentColor" d="M21 11V3h-8l3.29 3.29-10 10L3 13v8h8l-3.29-3.29 10-10z" />
                    </svg>
                  )}
                </button>
              )}
              {!page && (
                <button type="button" className="kai-panel-icon kai-panel-close" onClick={close} aria-label="Close KAI" title="Close">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              )}
            </div>
          </div>
          <div className="kai-log" ref={logRef}>
            {messages.length === 0 && (
              <div className="kai-welcome">
                <KaiAvatar size={56} state={busy ? "thinking" : "idle"} className="kai-orb-raised-lg" />
                <div className="kai-welcome-title">KAI</div>
                <div className="kai-welcome-text">{greeting.message}</div>
                <div className="kai-prompts">
                  {(greeting.examples || []).map((prompt) => (
                    <button key={prompt} type="button" disabled={busy} onClick={() => ask(prompt)}>{prompt}</button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((message, index) => (
              <div key={index} className={`kai-bubble kai-bubble--${message.role}`}>
                <div>{message.cards && message.cards.length ? message.text.split("\n")[0] : message.text}</div>
                {(message.cards || []).map((card) => {
                  const invoice = card.kind !== "purchase" && card.peculiar_no
                    ? `/sale/invoice/${encodeURIComponent(card.peculiar_no)}${publicKey ? `?pk=${publicKey}` : ""}`
                    : "";
                  return (
                    <div className="kai-order" key={card.id || card.no}>
                      {invoice ? (
                        <a href={invoice} target="_blank" rel="noreferrer">{card.no}</a>
                      ) : (
                        <strong>{card.no}</strong>
                      )}
                      {card.total != null ? ` $${card.total}` : ""}
                      {card.customer ? `, ${card.customer}` : ""}
                      {card.email ? `, email ${card.email}` : ""}
                      {card.created_at ? `, created ${card.created_at}` : ""}
                      {card.created_by ? ` by ${card.created_by}` : ""}
                    </div>
                  );
                })}
                {message.more && (
                  <button type="button" className="kai-more" onClick={() => ask("show more")}>Show more</button>
                )}
                <button type="button" className="kai-reply" onClick={() => setReplyTo({ index, text: message.text })}>
                  Reply
                </button>
              </div>
            ))}
            {busy && (
              <div className="kai-bubble kai-bubble--kai kai-bubble--thinking">
                <KaiAvatar size={24} state="thinking" /> Thinking…
              </div>
            )}
          </div>
          {replyTo && (
            <div className="kai-replying">
              <span>Replying to: {replyTo.text.slice(0, 80)}</span>
              <button type="button" onClick={() => setReplyTo(null)} aria-label="Cancel reply">×</button>
            </div>
          )}
          {wipeOpen && (
            <div className="kai-wipe" role="dialog" aria-label="Clear chat">
              <p>All chat will be wiped out.</p>
              <label>
                <input type="checkbox" checked={wipeOk} onChange={(event) => setWipeOk(event.target.checked)} />
                I understand. Clear this history.
              </label>
              <div className="kai-wipe-actions">
                <button type="button" onClick={() => { setWipeOpen(false); setWipeOk(false); }}>Cancel</button>
                <button type="button" className="kai-wipe-clear" disabled={!wipeOk || busy} onClick={resetHistory}>Clear</button>
              </div>
            </div>
          )}
          <form className="kai-form" onSubmit={(event) => { event.preventDefault(); ask(text); }}>
            <textarea
              rows={large ? 4 : 2}
              value={text}
              placeholder={replyTo ? "Reply to that message…" : "Ask about sales, payments, stock…"}
              onChange={(event) => setText(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  ask(text);
                }
              }}
            />
            <button type="submit" disabled={busy || !text.trim()} aria-label="Send">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          </form>
        </section>
      )}
      {!page && !isOpen && (
        /* Design handoff: the launcher IS the orb — 56px, idle tide always
           running, no pill, no label. The button is only the hit target. */
        <button type="button" className="kai-fab" onClick={toggle} aria-label="Open KAI">
          <KaiAvatar size={56} state="idle" className="kai-orb-raised-lg" />
        </button>
      )}
    </>
  );
}

export function KaiPage() {
  const authToken = useSelector((state) => state.auth.authToken);
  return (
    <div className="kai-page">
      <KaiWidget authToken={authToken} page />
    </div>
  );
}
